/**
 * Bibliothèque de templates de KPIs custom prêts à cloner.
 *
 * Chaque template contient un `payload` partiel qui sera injecté dans le
 * formulaire du wizard (CustomKpiEditor). Les paths SQL (`t.<col>`,
 * `r1.<col>`, …) sont **stables** dès que :
 *   - `t` est toujours l'alias du schéma principal
 *   - les relations sont assignées dans l'ordre de `relations_chain` :
 *     première = `r1`, deuxième = `r2`, etc.
 *
 * Les libellés (`label`, `description`) sont des clés i18n résolues à
 * l'affichage côté composant.
 */
import { CreateCustomKpiPayload } from './customKpi.interfaces';

export type CustomKpiTemplateGroup = 'sales' | 'catalog' | 'mix' | 'cart' | 'customer';

export interface CustomKpiTemplate {
  id: string;
  group: CustomKpiTemplateGroup;
  icon: string;
  /** clé i18n du nom — résolue côté composant via t() */
  labelKey: string;
  /** clé i18n de la description — résolue côté composant via t() */
  descriptionKey: string;
  /**
   * Payload prérempli. Le wizard fera une fusion non-destructive
   * (l'utilisateur peut ensuite modifier).
   *
   * Les `name` / `label` ne sont PAS pré-remplis ici : on demande au
   * marchand de nommer son KPI via le wizard.
   */
  payload: Omit<CreateCustomKpiPayload, 'name'>;
}

export const CUSTOM_KPI_TEMPLATES: CustomKpiTemplate[] = [
  // ─────────────────────────────────────────────────────────────────
  // SALES — sur la source `orders`
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'sales-revenue-daily',
    group: 'sales',
    icon: 'far fa-money-bill-trend-up',
    labelKey: 'customStats.templates.sales-revenue-daily.label',
    descriptionKey: 'customStats.templates.sales-revenue-daily.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: [],
      measures: [
        {
          type: 'sum',
          field: 't.amount',
          format: 'currency',
          label: { fr: 'CA total', en: 'Total revenue' },
        },
      ],
      dimensions: [],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-avg-basket',
    group: 'sales',
    icon: 'far fa-receipt',
    labelKey: 'customStats.templates.sales-avg-basket.label',
    descriptionKey: 'customStats.templates.sales-avg-basket.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: [],
      measures: [
        {
          type: 'avg',
          field: 't.amount',
          format: 'currency',
          label: { fr: 'Panier moyen', en: 'Average basket' },
        },
      ],
      dimensions: [
        {
          field: 't.status',
          label: { fr: 'Statut', en: 'Status' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-orders-by-hour',
    group: 'sales',
    icon: 'far fa-clock',
    labelKey: 'customStats.templates.sales-orders-by-hour.label',
    descriptionKey: 'customStats.templates.sales-orders-by-hour.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Commandes', en: 'Orders' },
        },
      ],
      dimensions: [
        {
          field: 't.order_hour',
          label: { fr: 'Heure', en: 'Hour' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-orders-by-dow',
    group: 'sales',
    icon: 'far fa-calendar-week',
    labelKey: 'customStats.templates.sales-orders-by-dow.label',
    descriptionKey: 'customStats.templates.sales-orders-by-dow.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Commandes', en: 'Orders' },
        },
      ],
      dimensions: [
        {
          field: 't.order_dow',
          label: { fr: 'Jour de la semaine', en: 'Day of week' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-by-carrier',
    group: 'sales',
    icon: 'far fa-truck',
    labelKey: 'customStats.templates.sales-by-carrier.label',
    descriptionKey: 'customStats.templates.sales-by-carrier.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: ['carrier'],
      measures: [
        {
          type: 'sum',
          field: 't.amount',
          format: 'currency',
          label: { fr: 'CA', en: 'Revenue' },
        },
      ],
      dimensions: [
        {
          field: 'r1.name',
          label: { fr: 'Transporteur', en: 'Carrier' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-voucher-usage',
    group: 'sales',
    icon: 'far fa-tag',
    labelKey: 'customStats.templates.sales-voucher-usage.label',
    descriptionKey: 'customStats.templates.sales-voucher-usage.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Commandes', en: 'Orders' },
        },
      ],
      dimensions: [
        {
          field: 't.has_voucher_label',
          label: { fr: 'Avec code promo', en: 'Has voucher' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // MIX — produits / marques (source orders_products)
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'mix-top-products',
    group: 'mix',
    icon: 'far fa-trophy',
    labelKey: 'customStats.templates.mix-top-products.label',
    descriptionKey: 'customStats.templates.mix-top-products.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders_products',
      relations_chain: [],
      measures: [
        {
          type: 'sum',
          field: 't.qty',
          format: 'integer',
          label: { fr: 'Quantité vendue', en: 'Quantity sold' },
        },
      ],
      dimensions: [
        {
          field: 't.id_product',
          label: { fr: 'Produit', en: 'Product' },
          position: 0,
        },
      ],
      time_field: 't.date',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'mix-revenue-by-brand',
    group: 'mix',
    icon: 'far fa-tags',
    labelKey: 'customStats.templates.mix-revenue-by-brand.label',
    descriptionKey: 'customStats.templates.mix-revenue-by-brand.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders_products',
      relations_chain: ['manufacturer'],
      measures: [
        {
          type: 'sum',
          field: 't.line_total',
          format: 'currency',
          label: { fr: 'CA', en: 'Revenue' },
        },
      ],
      dimensions: [
        {
          field: 'r1.name',
          label: { fr: 'Marque', en: 'Brand' },
          position: 0,
        },
      ],
      time_field: 't.date',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'mix-revenue-by-category',
    group: 'mix',
    icon: 'far fa-folder',
    labelKey: 'customStats.templates.mix-revenue-by-category.label',
    descriptionKey:
      'customStats.templates.mix-revenue-by-category.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders_products',
      relations_chain: ['main_category'],
      measures: [
        {
          type: 'sum',
          field: 't.line_total',
          format: 'currency',
          label: { fr: 'CA', en: 'Revenue' },
        },
      ],
      dimensions: [
        {
          field: 'r1.category_name',
          label: { fr: 'Catégorie', en: 'Category' },
          position: 0,
        },
      ],
      time_field: 't.date',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'mix-orders-with-brand-filter',
    group: 'mix',
    icon: 'far fa-filter',
    labelKey: 'customStats.templates.mix-orders-with-brand-filter.label',
    descriptionKey:
      'customStats.templates.mix-orders-with-brand-filter.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: ['products'],
      measures: [
        {
          type: 'sum',
          field: 't.amount',
          format: 'currency',
          label: { fr: 'CA', en: 'Revenue' },
        },
      ],
      dimensions: [],
      time_field: 't.date_creation',
      time_field_explicit: true,
      // Filtre exemple — le marchand devra changer 'NIKE' par sa marque cible
      filters: [
        {
          field: 'r1.id_manufacturer',
          operator: 'eq',
          value: 'NIKE',
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // CATALOG — products
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'catalog-stock-value',
    group: 'catalog',
    icon: 'far fa-warehouse',
    labelKey: 'customStats.templates.catalog-stock-value.label',
    descriptionKey: 'customStats.templates.catalog-stock-value.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'products',
      relations_chain: [],
      measures: [
        {
          type: 'sum',
          field: 't.stock_value',
          format: 'currency',
          label: { fr: 'Valeur stock', en: 'Stock value' },
        },
      ],
      dimensions: [],
      time_field: 't.date_modification',
      time_field_explicit: true,
      filters: [
        {
          field: 't.active_label',
          operator: 'eq',
          value: 'Actif',
        },
      ],
    },
  },
  {
    id: 'catalog-out-of-stock',
    group: 'catalog',
    icon: 'far fa-circle-exclamation',
    labelKey: 'customStats.templates.catalog-out-of-stock.label',
    descriptionKey: 'customStats.templates.catalog-out-of-stock.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'products',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Produits en rupture', en: 'Out-of-stock products' },
        },
      ],
      dimensions: [],
      time_field: 't.date_modification',
      time_field_explicit: true,
      filters: [
        {
          field: 't.in_stock_label',
          operator: 'eq',
          value: 'Non',
        },
      ],
    },
  },
  {
    id: 'catalog-avg-price-by-brand',
    group: 'catalog',
    icon: 'far fa-money-bill-1',
    labelKey: 'customStats.templates.catalog-avg-price-by-brand.label',
    descriptionKey:
      'customStats.templates.catalog-avg-price-by-brand.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'products',
      relations_chain: ['manufacturer'],
      measures: [
        {
          type: 'avg',
          field: 't.price',
          format: 'currency',
          label: { fr: 'Prix moyen', en: 'Average price' },
        },
      ],
      dimensions: [
        {
          field: 'r1.name',
          label: { fr: 'Marque', en: 'Brand' },
          position: 0,
        },
      ],
      time_field: 't.date_modification',
      time_field_explicit: true,
      filters: [],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // CART — paniers
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'cart-abandon-rate',
    group: 'cart',
    icon: 'far fa-cart-arrow-down',
    labelKey: 'customStats.templates.cart-abandon-rate.label',
    descriptionKey: 'customStats.templates.cart-abandon-rate.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'carts',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Paniers', en: 'Carts' },
        },
      ],
      dimensions: [
        {
          field: 't.cart_status',
          label: { fr: 'Statut', en: 'Status' },
          position: 0,
        },
      ],
      time_field: 't.date',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'cart-abandoned-value',
    group: 'cart',
    icon: 'far fa-coins',
    labelKey: 'customStats.templates.cart-abandoned-value.label',
    descriptionKey: 'customStats.templates.cart-abandoned-value.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'carts',
      relations_chain: [],
      measures: [
        {
          type: 'sum',
          field: 't.amount',
          format: 'currency',
          label: { fr: 'Valeur abandonnée', en: 'Abandoned value' },
        },
      ],
      dimensions: [],
      time_field: 't.date',
      time_field_explicit: true,
      filters: [
        {
          field: 't.cart_status',
          operator: 'eq',
          value: 'Abandonné',
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // CUSTOMER — contacts
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'customer-by-country',
    group: 'customer',
    icon: 'far fa-earth-europe',
    labelKey: 'customStats.templates.customer-by-country.label',
    descriptionKey: 'customStats.templates.customer-by-country.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'contacts',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Contacts', en: 'Contacts' },
        },
      ],
      dimensions: [
        {
          field: 't.contact_country',
          label: { fr: 'Pays', en: 'Country' },
          position: 0,
        },
      ],
      time_field: 't.created_on_shop_at',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'customer-optin-email',
    group: 'customer',
    icon: 'far fa-envelope-circle-check',
    labelKey: 'customStats.templates.customer-optin-email.label',
    descriptionKey: 'customStats.templates.customer-optin-email.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'contacts',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Contacts', en: 'Contacts' },
        },
      ],
      dimensions: [
        {
          field: 't.optin_email_label',
          label: { fr: 'Opt-in email', en: 'Email opt-in' },
          position: 0,
        },
      ],
      time_field: 't.created_on_shop_at',
      time_field_explicit: true,
      filters: [],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // SALES — basés sur les nouvelles sources/relations (statut, vouchers)
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'sales-revenue-by-status',
    group: 'sales',
    icon: 'far fa-clipboard-check',
    labelKey: 'customStats.templates.sales-revenue-by-status.label',
    descriptionKey:
      'customStats.templates.sales-revenue-by-status.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: ['order_status'],
      measures: [
        {
          type: 'sum',
          field: 't.amount_eur',
          format: 'currency',
          label: { fr: 'CA (EUR)', en: 'Revenue (EUR)' },
        },
      ],
      dimensions: [
        {
          field: 'r1.status_name',
          label: { fr: 'Statut', en: 'Status' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-voucher-by-origin',
    group: 'sales',
    icon: 'far fa-ticket',
    labelKey: 'customStats.templates.sales-voucher-by-origin.label',
    descriptionKey:
      'customStats.templates.sales-voucher-by-origin.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'vouchers',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Codes', en: 'Vouchers' },
        },
      ],
      dimensions: [
        {
          field: 't.origin_label',
          label: { fr: 'Origine', en: 'Origin' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'sales-spm-voucher-discount-value',
    group: 'sales',
    icon: 'far fa-gift',
    labelKey: 'customStats.templates.sales-spm-voucher-discount-value.label',
    descriptionKey:
      'customStats.templates.sales-spm-voucher-discount-value.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'vouchers',
      relations_chain: [],
      measures: [
        {
          type: 'sum',
          field: 't.amount',
          format: 'currency',
          label: {
            fr: 'Valeur réduction accordée',
            en: 'Granted discount value',
          },
        },
      ],
      dimensions: [],
      time_field: 't.date_creation',
      time_field_explicit: true,
      // Codes générés par ShopiMind (préfixe SPM_) ET effectivement utilisés.
      filters: [
        {
          field: 't.origin_label',
          operator: 'eq',
          value: 'ShopiMind',
        },
        {
          field: 't.used_label',
          operator: 'eq',
          value: 'Utilisé',
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // CATALOG / MIX — variantes
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'catalog-variants-out-of-stock',
    group: 'catalog',
    icon: 'far fa-shapes',
    labelKey: 'customStats.templates.catalog-variants-out-of-stock.label',
    descriptionKey:
      'customStats.templates.catalog-variants-out-of-stock.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'product_attributes',
      relations_chain: [],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Variantes en rupture', en: 'Out-of-stock variants' },
        },
      ],
      dimensions: [],
      time_field: 't.date_modification',
      time_field_explicit: true,
      filters: [
        {
          field: 't.in_stock_label',
          operator: 'eq',
          value: 'Non',
        },
      ],
    },
  },
  {
    id: 'mix-top-variants',
    group: 'mix',
    icon: 'far fa-trophy',
    labelKey: 'customStats.templates.mix-top-variants.label',
    descriptionKey: 'customStats.templates.mix-top-variants.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders_products',
      relations_chain: ['attribute'],
      measures: [
        {
          type: 'sum',
          field: 't.qty',
          format: 'integer',
          label: { fr: 'Quantité vendue', en: 'Quantity sold' },
        },
      ],
      dimensions: [
        {
          field: 'r1.combination_name',
          label: { fr: 'Variante', en: 'Variant' },
          position: 0,
        },
      ],
      time_field: 't.date',
      time_field_explicit: true,
      filters: [],
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // CUSTOMER — groupes clients
  // ─────────────────────────────────────────────────────────────────
  {
    id: 'customer-revenue-by-group',
    group: 'customer',
    icon: 'far fa-users-rectangle',
    labelKey: 'customStats.templates.customer-revenue-by-group.label',
    descriptionKey:
      'customStats.templates.customer-revenue-by-group.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'orders',
      relations_chain: ['main_group'],
      measures: [
        {
          type: 'sum',
          field: 't.amount_eur',
          format: 'currency',
          label: { fr: 'CA (EUR)', en: 'Revenue (EUR)' },
        },
      ],
      dimensions: [
        {
          field: 'r1.group_name',
          label: { fr: 'Groupe', en: 'Group' },
          position: 0,
        },
      ],
      time_field: 't.date_creation',
      time_field_explicit: true,
      filters: [],
    },
  },
  {
    id: 'customer-count-by-group',
    group: 'customer',
    icon: 'far fa-users',
    labelKey: 'customStats.templates.customer-count-by-group.label',
    descriptionKey:
      'customStats.templates.customer-count-by-group.description',
    payload: {
      source_type: 'system',
      source_schema_name: 'contacts',
      relations_chain: ['main_group'],
      measures: [
        {
          type: 'count',
          field: undefined,
          format: 'integer',
          label: { fr: 'Contacts', en: 'Contacts' },
        },
      ],
      dimensions: [
        {
          field: 'r1.group_name',
          label: { fr: 'Groupe', en: 'Group' },
          position: 0,
        },
      ],
      time_field: 't.created_on_shop_at',
      time_field_explicit: true,
      filters: [],
    },
  },
];

export const CUSTOM_KPI_TEMPLATE_GROUP_ORDER: CustomKpiTemplateGroup[] = [
  'sales',
  'mix',
  'catalog',
  'cart',
  'customer',
];
